// src/lib/filemaker-sync/taskLogs.ts
//
// Historical task log of the FileMaker 19.2.2 sync application.
// Per the user ask on 2026-07-14, the sync agent + AWS side
// surface their run history under /admin/filemaker-sync so the
// operator can debug issues and prove compliance.
//
// In production, fmmirror-mcp + the on-prem sync agent push run
// summaries here. v1 uses a deterministic seed so the page is
// non-empty.
//
// Shape mirrors what the eventual backend will return:
//
//   - each entry is one tick of one side (FM->AWS or AWS->FM)
//   - carries timing, status, affected row count, error
//   - linked to the agent that produced it (avc-fm-sync-agent,
//     fmmirror-mcp, ben-fm-writeback, agent-c, agent-f, etc.)

export type SyncTaskStatus = "ok" | "warning" | "error" | "running";

export type SyncAgent =
  | "avc-fm-sync-agent"     // on-prem FileMaker -> S3
  | "fmmirror-mcp"          // AWS SQS -> Postgres + Redpanda
  | "ben-fm-writeback"      // AWS Postgres -> FileMaker writeback
  | "agent-c"               // reconciliation (Agent C)
  | "agent-f";              // FileMaker schema-mapper (Agent F)

export type SyncDirection = "fm_to_aws" | "aws_to_fm" | "reconciliation" | "schema_probe";

export interface SyncTaskLog {
  id: string;
  /** The agent that produced this run. */
  agent: SyncAgent;
  /** Which direction this run moved data. */
  direction: SyncDirection;
  /** What was processed. For FM layouts, the layout key. For
   *  reconciliations, the data domain. */
  target: string;
  /** Outcome. */
  status: SyncTaskStatus;
  /** When the run started (ISO). */
  startedAt: string;
  /** Duration in milliseconds. `null` while still running. */
  durationMs: number | null;
  /** Rows inserted/updated/deleted. */
  rowsAffected: number;
  /** Errors captured during the run (truncated to 5 in v1). */
  errors: Array<{ code: string; message: string; count: number }>;
  /** Triggering cron / schedule entry for human-readable UI. */
  triggeredBy: "cron" | "manual" | "redpanda_event" | "sqs_event" | "boot";
  /** Most recent FileMaker layout timestamp observed (for
   *  fm_to_aws). `null` for the other directions. */
  lastFmTimestamp: string | null;
}

function isoMinusMinutes(min: number): string {
  return new Date(Date.now() - min * 60_000).toISOString();
}

// Deterministic seed of ~30 historical runs, mixed across
// agents, statuses, and directions. Sorted newest-first by the
// caller.
export const SEED_TASK_LOGS: SyncTaskLog[] = [
  {
    id: "TSK-00124",
    agent: "avc-fm-sync-agent",
    direction: "fm_to_aws",
    target: "customers",
    status: "ok",
    startedAt: isoMinusMinutes(1),
    durationMs: 4287,
    rowsAffected: 4,
    errors: [],
    triggeredBy: "cron",
    lastFmTimestamp: isoMinusMinutes(1),
  },
  {
    id: "TSK-00123",
    agent: "ben-fm-writeback",
    direction: "aws_to_fm",
    target: "SalesOrder",
    status: "ok",
    startedAt: isoMinusMinutes(7),
    durationMs: 1642,
    rowsAffected: 1,
    errors: [],
    triggeredBy: "redpanda_event",
    lastFmTimestamp: isoMinusMinutes(7),
  },
  {
    id: "TSK-00122",
    agent: "fmmirror-mcp",
    direction: "fm_to_aws",
    target: "repairs",
    status: "warning",
    startedAt: isoMinusMinutes(12),
    durationMs: 8810,
    rowsAffected: 6,
    errors: [{ code: "ORPHAN_FK", message: "FM-RepairID=117 references missing customer AccountID=5099", count: 1 }],
    triggeredBy: "sqs_event",
    lastFmTimestamp: isoMinusMinutes(12),
  },
  {
    id: "TSK-00121",
    agent: "avc-fm-sync-agent",
    direction: "fm_to_aws",
    target: "invoices",
    status: "ok",
    startedAt: isoMinusMinutes(15),
    durationMs: 6230,
    rowsAffected: 2,
    errors: [],
    triggeredBy: "cron",
    lastFmTimestamp: isoMinusMinutes(15),
  },
  {
    id: "TSK-00120",
    agent: "agent-f",
    direction: "schema_probe",
    target: "Customer",
    status: "ok",
    startedAt: isoMinusMinutes(18),
    durationMs: 1840,
    rowsAffected: 0,
    errors: [],
    triggeredBy: "cron",
    lastFmTimestamp: null,
  },
  {
    id: "TSK-00119",
    agent: "agent-c",
    direction: "reconciliation",
    target: "invoice-vs-bank",
    status: "ok",
    startedAt: isoMinusMinutes(120),
    durationMs: 47_812,
    rowsAffected: 0,
    errors: [],
    triggeredBy: "cron",
    lastFmTimestamp: null,
  },
  {
    id: "TSK-00118",
    agent: "avc-fm-sync-agent",
    direction: "fm_to_aws",
    target: "products",
    status: "ok",
    startedAt: isoMinusMinutes(35),
    durationMs: 5021,
    rowsAffected: 0,
    errors: [],
    triggeredBy: "cron",
    lastFmTimestamp: isoMinusMinutes(35),
  },
  {
    id: "TSK-00117",
    agent: "fmmirror-mcp",
    direction: "fm_to_aws",
    target: "invoices",
    status: "warning",
    startedAt: isoMinusMinutes(120),
    durationMs: 9921,
    rowsAffected: 2,
    errors: [{ code: "FX_STALE", message: "EUR-USD rate snapshot is 47 days old at detection time.", count: 1 }],
    triggeredBy: "sqs_event",
    lastFmTimestamp: isoMinusMinutes(120),
  },
  {
    id: "TSK-00116",
    agent: "ben-fm-writeback",
    direction: "aws_to_fm",
    target: "Quotation",
    status: "ok",
    startedAt: isoMinusMinutes(150),
    durationMs: 1203,
    rowsAffected: 1,
    errors: [],
    triggeredBy: "redpanda_event",
    lastFmTimestamp: isoMinusMinutes(150),
  },
  {
    id: "TSK-00115",
    agent: "avc-fm-sync-agent",
    direction: "fm_to_aws",
    target: "customers",
    status: "error",
    startedAt: isoMinusMinutes(240),
    durationMs: 30_000,
    rowsAffected: 0,
    errors: [
      { code: "HTTP_TIMEOUT", message: "FileMaker Server unreachable after 30s", count: 1 },
      { code: "DLQ", message: "Batch parked in dead-letter queue after 5 retries.", count: 1 },
    ],
    triggeredBy: "cron",
    lastFmTimestamp: null,
  },
  {
    id: "TSK-00114",
    agent: "fmmirror-mcp",
    direction: "fm_to_aws",
    target: "producers",
    status: "ok",
    startedAt: isoMinusMinutes(2),
    durationMs: 3340,
    rowsAffected: 1,
    errors: [],
    triggeredBy: "sqs_event",
    lastFmTimestamp: isoMinusMinutes(2),
  },
  {
    id: "TSK-00113",
    agent: "ben-fm-writeback",
    direction: "aws_to_fm",
    target: "SalesOrder",
    status: "warning",
    startedAt: isoMinusMinutes(190),
    durationMs: 4812,
    rowsAffected: 0,
    errors: [{ code: "FM_REJECT", message: "Layout SalesOrder rejected row (vendor terms out of whitelist).", count: 1 }],
    triggeredBy: "redpanda_event",
    lastFmTimestamp: isoMinusMinutes(190),
  },
];

export const SYNC_AGENT_LABEL: Record<SyncAgent, string> = {
  "avc-fm-sync-agent": "avc-fm-sync-agent",
  "fmmirror-mcp": "fmmirror-mcp",
  "ben-fm-writeback": "ben-fm-writeback",
  "agent-c": "Agent C (reconciliation)",
  "agent-f": "Agent F (vendor mapper)",
};

export const SYNC_DIRECTION_LABEL: Record<SyncDirection, string> = {
  fm_to_aws: "FM → AWS",
  aws_to_fm: "AWS → FM",
  reconciliation: "Reconciliation",
  schema_probe: "Schema probe",
};

export const SYNC_STATUS_TONE: Record<SyncTaskStatus, string> = {
  ok: "bg-accent-green/15 text-accent-green",
  warning: "bg-accent-amber/15 text-accent-amber",
  error: "bg-accent-rose/15 text-accent-rose",
  running: "bg-accent-indigo/15 text-accent-indigo",
};
